import {Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowRotateRight,
  faArrowUpRightFromSquare,
  faBoxArchive,
  faCalendarClock,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faCopy,
  faEllipsisVertical,
  faLinkSlash,
  faLock,
  faPaperPlaneTop,
  faShareNodes,
  faUserPen,
  faPlay,
  faShieldCheck,
  faThumbtack,
  faThumbtackSlash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {CheckDefinition} from '../../my-checks.service';
import {PermissionService} from '../../../../services/permission/permission.service';
import {CatalogSubmission, catalogSubmissionStatusLabel} from '../../../../services/catalog.service';
import {CheckRunScheduleView} from '../../../../api/model/checkRunScheduleView';
import {formatBerlin, pauseReasonLabel} from '../../../../services/check-run-schedule/check-run-schedule.format';

export type CheckCardAction =
  'execute'
  | 'open'
  | 'duplicate'
  | 'edit'
  | 'settings'
  /**
   * Zentrale Bereitstellung (Adminfunktion). Absichtlich nicht „share": das Teilen zwischen Projekten
   * für normale Nutzer ist etwas anderes (Live-Verlinkung statt Kopie) — der Name hält die beiden im
   * Code auseinander.
   */
  | 'provide-globally'
  /** Mit anderen Projekten der Organisation teilen – nur im Eigentümerprojekt. */
  | 'share'
  /** Einen in dieses Projekt geteilten myCheck wieder aus diesem Projekt entfernen. */
  | 'unshare-here'
  /** Ein anderes verlinktes Projekt zum Eigentümer machen – nur im Eigentümerprojekt. */
  | 'transfer-ownership'
  | 'archive'
  | 'restore'
  | 'view-workflow'
  | 'activate'
  | 'deactivate'
  | 'pin'
  | 'unpin'
  /** Zeitplan dieses Prüflaufs im aktuellen Projekt anlegen oder bearbeiten. */
  | 'schedule';

@Component({
  selector: 'my-checks-card',
  standalone: true,
  imports: [
    FaIconComponent,
    BicButtonComponent,
    BicBadgeComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    TooltipDirective,
    DatePipe,
  ],
  templateUrl: './check-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./check-card.component.scss'],
})
export class CheckCardComponent {
  @Input({required: true}) check!: CheckDefinition;
  @Input() description = '';
  @Input() actionLoading = false;
  @Input() pinned = false;
  /** Card zeigt einen archivierten myCheck – reduziertes Menü (nur "Wiederherstellen"). */
  @Input() archived = false;
  /** Projekt der Übersicht – unterscheidet Eigentümer- und Zielprojekt eines geteilten myChecks. */
  @Input() currentProjectId?: number;
  /**
   * Katalog: Darf aus dieser Stage eingereicht werden? Kommt einmalig von der Übersicht
   * ({@code GET /workflows/catalog/config}), damit nicht jede Karte selbst fragt.
   */
  @Input() catalogSubmissionAllowed = false;
  /** Letzte Katalog-Einreichung dieses Prüflaufs (nur für Admins geladen), sonst {@code null}. */
  @Input() catalogSubmission: CatalogSubmission | null = null;
  /**
   * Duplikat einer Katalog-Kopie: höhere Katalog-Version, die als Freigabe vorliegt – sonst
   * {@code null}. Die Übersicht vergleicht dafür {@code sourceCatalogVersion} mit der Freigabe.
   */
  @Input() newCatalogVersion: number | null = null;
  /** Zeitplan dieses Prüflaufs im aktuellen Projekt, sonst {@code null}. */
  @Input() schedule: CheckRunScheduleView | null = null;

  @Output() action = new EventEmitter<{ action: CheckCardAction; check: CheckDefinition }>();

  @ViewChild('cardMenu') cardMenu!: BicDropdownComponent;

  protected readonly permissionService = inject(PermissionService);

  protected readonly faLock = faLock;
  protected readonly faShieldCheck = faShieldCheck;

  /**
   * Der Check ist zentral bereitgestellt (Freigabe eines Plattform-Admins): schreibgeschützt, aber
   * <b>ausführbar</b> und duplizierbar.
   *
   * <p>Nicht bearbeitbar, weil die Definition dem Adminbereich gehört und nicht diesem Projekt —
   * Anpassungen laufen über ein Duplikat.
   */
  protected get isShared(): boolean {
    return !!this.check.shared;
  }

  /**
   * Der myCheck gehört einem anderen Projekt und ist in dieses geteilt: ausführbar, duplizierbar und
   * anheftbar, bearbeitet wird er nur im Eigentümerprojekt.
   */
  protected get isLinkedIn(): boolean {
    return this.check.projectId != null
      && this.currentProjectId != null
      && this.check.projectId !== this.currentProjectId;
  }

  /** Mit mindestens einem weiteren Projekt geteilt (aus Sicht egal welchen Projekts). */
  protected get isSharedWithProjects(): boolean {
    return (this.check.sharedProjects?.length ?? 0) > 1;
  }

  /** „A (Eigentümer), B, C" – Eigentümer kommt vom Backend bereits zuerst. */
  protected get sharedProjectsLabel(): string {
    return (this.check.sharedProjects ?? [])
      .map(p => p.owner ? `${p.name} (Eigentümer)` : p.name)
      .join(', ');
  }

  /**
   * Teilen geht nur mit eigenen Prüfläufen aus dem Eigentümerprojekt. Ob der Nutzer die Rollen für
   * ein Zielprojekt hat, zeigt erst der Dialog – das weiß nur das Backend.
   */
  protected get canShare(): boolean {
    return !this.isShared && !this.isLinkedIn && this.check.origin === 'CUSTOM' && this.canManage;
  }

  /**
   * Prüfrechte – myChecks ausführen. Gilt auch für zentral bereitgestellte Checks: sie laufen im
   * Projekt des Empfängers, mit seinen Dateien und auf sein Kontingent.
   */
  protected get canExecute(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE');
  }

  /** myCheck verwalten – erstellen, duplizieren, teilen, bearbeiten, anheften. */
  protected get canManage(): boolean {
    return !this.isShared && this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  protected readonly noDeleteTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um diesen Prüflauf zu archivieren.';

  protected readonly noManageTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um diesen Prüflauf zu verwalten.';
  /** Eigentümer ändern: nur ein geteilter Prüflauf, nur im Eigentümerprojekt. */
  protected get canTransferOwnership(): boolean {
    return this.isSharedWithProjects && !this.isLinkedIn && this.canManage;
  }

  protected readonly faUserPen = faUserPen;
  protected readonly faShareNodes = faShareNodes;
  protected readonly faLinkSlash = faLinkSlash;
  protected readonly lockTooltip = 'Dieser Prüflauf wurde von der BIC freigegeben und kann nicht bearbeitet werden. Er kann nur dupliziert werden.';
  protected readonly faBoxArchive = faBoxArchive;

  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faPlay = faPlay;
  protected readonly faCopy = faCopy;

  /** Duplizieren ist auf einer Freigabe erlaubt – gebraucht wird nur das Verwalten-Recht. */
  protected get canDuplicate(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /**
   * Zentrale Bereitstellung ist ein plattform-globales Admin-Recht, kein Projektrecht.
   *
   * <p>Nicht-Admins sehen den Eintrag <b>gar nicht</b> (statt ausgegraut): das Privileg wird nur per
   * DB-Eintrag vergeben, ein gesperrter Eintrag würde also eine Tür versprechen, die es nicht gibt.
   */
  protected get canProvideGlobally(): boolean {
    // Aus einem anderen Projekt geteilt: bereitgestellt wird aus dem Eigentümerprojekt heraus.
    return !this.isLinkedIn && this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN');
  }

  /**
   * Menüeintrag „Zentral bereitstellen" = Katalog-Einreichung. Zusätzlich zum Admin-Recht muss die
   * Stage das Einreichen erlauben (nur beta, dev zum Testen) – sonst ist der Eintrag unsichtbar.
   */
  protected get canSubmitToCatalog(): boolean {
    return this.canProvideGlobally && this.catalogSubmissionAllowed;
  }

  /** Badge-Text zum letzten Stand: „Zentral bereitgestellt", aus der Einreichungszeit „Eingereicht"/„Abgelehnt". */
  protected get catalogBadgeLabel(): string | null {
    return this.catalogSubmission ? catalogSubmissionStatusLabel(this.catalogSubmission.status) : null;
  }

  protected get catalogBadgeTooltip(): string {
    const submission = this.catalogSubmission;
    if (!submission) return '';
    if (submission.status === 'REJECTED') {
      return submission.rejectReason
        ? `Im Admin Center abgelehnt: ${submission.rejectReason}`
        : 'Im Admin Center abgelehnt.';
    }
    if (submission.status === 'INSTALLED') {
      return 'In dieser Stage zentral bereitgestellt – nach Live übernimmt das Admin Center.';
    }
    return 'Im Admin Center zur Freigabe eingereicht.';
  }

  protected get newVersionTooltip(): string {
    return `Version ${this.newCatalogVersion} ist unter „Vorgefertigt" verfügbar. Dieses Duplikat bleibt `
      + 'unverändert – duplizieren Sie die neue Fassung, um sie zu übernehmen.';
  }
  protected readonly faPaperPlaneTop = faPaperPlaneTop;
  protected readonly faArrowRotateRight = faArrowRotateRight;

  /** myCheck archivieren. Eine Freigabe gehört einem anderen Mandanten. */
  protected get canDelete(): boolean {
    return !this.isShared && this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_DELETE');
  }
  protected readonly faThumbtack = faThumbtack;
  protected readonly faThumbtackSlash = faThumbtackSlash;
  protected readonly faClock = faClock;
  protected readonly faCalendarClock = faCalendarClock;
  protected readonly noScheduleTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um Zeitpläne zu verwalten.';

  /** „Geplant“ bzw. „Pausiert“ in der Metazeile; ein beendeter Zeitplan zeigt nichts. */
  protected get scheduleLabel(): string | null {
    if (this.schedule?.status === 'ACTIVE') return 'Geplant';
    if (this.schedule?.status === 'PAUSED') return 'Pausiert';
    return null;
  }

  protected get scheduleTooltip(): string {
    const schedule = this.schedule;
    if (!schedule) return '';
    if (schedule.status === 'PAUSED') {
      const reason = pauseReasonLabel(schedule.pauseReason);
      return schedule.pauseDetail ? `Pausiert: ${reason} – ${schedule.pauseDetail}` : `Pausiert: ${reason}`;
    }
    return schedule.nextRunAt
      ? `Nächste Ausführung: ${formatBerlin(schedule.nextRunAt)}`
      : 'Keine weitere Ausführung geplant';
  }

  /** Bevorzugt "zuletzt geändert"; fällt auf das Erstellungsdatum zurück, wenn keins bekannt ist. */
  protected get lastChangedAt(): string | Date | undefined {
    return this.check.updatedAt ?? this.check.createdAt;
  }
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;

  /** Einsatzbereit (vollständig & aktiv) → grüner Haken; sonst gelbes Warn-Icon. */
  protected get isActive(): boolean {
    return this.check.status === 'ACTIVE';
  }

  /** Tooltip-Text des Status-Icons. */
  protected get statusTooltip(): string {
    return this.isActive
      ? 'Bereit zum Ausführen'
      : 'Anpassung notwendig';
  }

  /** Badge-Konfiguration je nach Herkunft des Checks (bereitgestellt vs. geteilt vs. BIC vs. myCheck). */
  protected get originBadge(): { label: string; bgClass: string; textClass: string } {
    if (this.isShared) {
      // „Vorgefertigt" statt „Geteilt": derselbe Begriff wie der Abschnittskopf und wie in der
      // Prüfregelverwaltung. „Geteilt" steht für das Teilen zwischen Projekten (unten).
      return {label: 'Vorgefertigt', bgClass: 'bg-green-100', textClass: 'text-gray-700'};
    }
    if (this.isLinkedIn) {
      return {label: 'Geteilt', bgClass: 'bg-blue-100', textClass: 'text-blue-900'};
    }
    return this.check.origin === 'BIC'
      ? {label: 'BIC Check', bgClass: 'bg-gray-200', textClass: 'text-gray-700'}
      : {label: 'Prüflauf', bgClass: 'bg-gray-500', textClass: 'text-white'};
  }

  onAction(action: CheckCardAction): void {
    this.cardMenu?.hide();
    this.action.emit({action, check: this.check});
  }
}
